import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "../ui/command";
import { Search, Zap } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { useInstitution } from "../../context/InstitutionContext";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { useTenantModules } from "../../lib/useTenantModules";

/**
 * Cmd-K (Ctrl-K) command palette — fuzzy search over every sidebar
 * destination + recently-visited paths. Keyboard-first navigation for power
 * users (deans, registrars, principal).
 * Role filtering matches sidebar visibility (NAV-INV-014).
 */

/**
 * CommandPalette destinations — kept in sync with Sidebar enterprise IA.
 * Implements: ACADEMIAOS_ENTERPRISE_NAVIGATION_DESIGN_V1.0_FROZEN (N2)
 * Groups mirror the 8 canonical sidebar sections (NAV-INV-014).
 */
const DESTINATIONS = [
  // ── Home
  { label: "Home",                          path: "/",                       group: "Home" },

  // ── Students (SDR-005: "Student & Academic Ops" retired)
  { label: "Student Directory",             path: "/core/students",          group: "Students",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager", "faculty", "instructor", "dean"],
    module: "01.02" },
  { label: "Notices",                       path: "/core/notices",           group: "Students",
    module: "01.02" },

  // ── Admissions (SDR-008 retired; F-004 DC-002: career_services added to Pipeline, excluded from Applications)
  { label: "Applications",                  path: "/admissions/applications", group: "Admissions",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager"] },
  { label: "Pipeline",                      path: "/enroll",                 group: "Admissions",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager", "faculty", "instructor", "career_services"],
    module: "01.01" },
  { label: "Analytics",                     path: "/enroll/analytics",       group: "Admissions",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager"],
    module: "01.01" },

  // ── Academics
  { label: "Academic Structure",            path: "/academic-structure",     group: "Academics",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager"] },
  { label: "Timetable",                     path: "/core/timetable",         group: "Academics",
    module: "01.02" },
  { label: "Attendance",                    path: "/core/attendance",        group: "Academics",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "programme_manager", "registrar", "dean"],
    module: "01.02" },
  { label: "Attendance Report",             path: "/core/attendance/report", group: "Academics",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "dean"],
    module: "01.02" },

  // ── Teaching & Learning
  { label: "LMS",                           path: "/learn",                  group: "Teaching & Learning",
    module: "04.01" },
  { label: "Content Studio",               path: "/content-studio",         group: "Teaching & Learning",
    roles: ["super_admin", "institution_admin", "faculty", "instructor"],
    module: "04.01" },
  { label: "Assessments",                  path: "/assessments",            group: "Teaching & Learning",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "programme_manager"],
    module: "04.01" },

  // ── Examinations
  { label: "Psychometrics",                path: "/psychometrics",          group: "Examinations",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "programme_manager"],
    module: "04.01" },

  // ── Finance (FM1 canonical routes — /core/fees is a compatibility redirect)
  { label: "Finance Overview",               path: "/finance/overview",       group: "Finance",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager",
            "finance_officer", "finance_manager", "cfo"],
    module: "01.02" },
  { label: "Fees & Student Accounts",        path: "/finance/accounts",       group: "Finance",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager",
            "finance_officer", "finance_manager", "cfo"],
    module: "01.02" },
  { label: "My Finance",                     path: "/my-finance",             group: "Finance",
    roles: ["student"],
    module: "01.02" },

  // ── People
  { label: "Faculty Development",           path: "/people",                 group: "People",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "hr_workforce_planner", "executive_leadership"],
    module: "06.01" },

  // ── Research
  { label: "Research Projects",            path: "/research",               group: "Research",
    module: "07.01" },

  // ── Quality & Accreditation (SDR-004: NAAC Dashboard → Accreditation Dashboard)
  { label: "Accreditation Dashboard",       path: "/comply",                 group: "Quality & Accreditation",
    roles: ["super_admin", "institution_admin", "compliance_officer", "programme_manager"],
    module: "09.01" },
  { label: "OBE Framework",                path: "/comply/obe",             group: "Quality & Accreditation",
    roles: ["super_admin", "institution_admin", "compliance_officer", "ai_governance_admin", "faculty", "instructor", "dean", "programme_manager"],
    module: "09.01" },

  // ── Campus
  { label: "Visitors & Incidents",          path: "/safe",                   group: "Campus",
    module: "08.03" },
  { label: "Sustainability",               path: "/green",                  group: "Campus",
    roles: ["super_admin", "institution_admin", "executive_leadership"],
    module: "08.03" },

  // ── Engagement
  { label: "Career & Placement",           path: "/launch",                 group: "Engagement",
    module: "01.06" },
  { label: "Placement Drives",             path: "/launch/drives",          group: "Engagement",
    roles: ["super_admin", "institution_admin", "registrar", "career_services", "programme_manager"],
    module: "01.06" },
  { label: "Alumni Network",               path: "/alumni-network",         group: "Engagement",
    module: "01.06" },

  // ── Insights (REC-B: Analytics→Reports; Executive Briefing added)
  { label: "Institutional Intelligence",   path: "/insights",               group: "Insights",
    roles: ["super_admin", "institution_admin", "executive_leadership"],
    module: "10.03" },
  { label: "Reports",                      path: "/analytics",              group: "Insights",
    roles: ["super_admin", "institution_admin", "executive_leadership", "compliance_officer", "ai_governance_admin"],
    module: "10.03" },
  { label: "Executive Briefing",           path: "/exec-briefing",          group: "Insights",
    roles: ["super_admin", "institution_admin", "executive_leadership"] },
  { label: "Command Centre",               path: "/command",                group: "Insights",
    module: "10.03" },

  // ── AI & Agents
  { label: "Chat",                         path: "/ai",                     group: "AI & Agents",
    module: "10.05" },
  { label: "Knowledge Base",               path: "/ai/knowledge",           group: "AI & Agents",
    roles: ["super_admin", "institution_admin", "faculty", "instructor", "registrar", "programme_manager", "compliance_officer", "ai_governance_admin"],
    module: "10.05" },
  { label: "Student Assistant",            path: "/student-assistant",      group: "AI & Agents",
    module: "01.06" },

  // ── Administration (F-003 DC-001: student MUST NOT see Workflows or Settings)
  { label: "Institution Setup",            path: "/institution-setup",      group: "Administration",
    roles: ["super_admin", "institution_admin"] },
  { label: "Users & Roles",               path: "/users-roles",            group: "Administration",
    roles: ["super_admin", "institution_admin"] },
  { label: "Branding & Module Names",      path: "/admin/tenant-config",    group: "Administration",
    roles: ["super_admin", "institution_admin"] },
  { label: "Onboarding Wizard",            path: "/onboarding",             group: "Administration",
    roles: ["super_admin", "institution_admin"] },
  { label: "Workflows",                    path: "/workflows",              group: "Administration",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager", "faculty", "instructor", "compliance_officer", "ai_governance_admin", "career_services", "training_manager", "hr_workforce_planner", "line_manager", "executive_leadership"] },
  { label: "Settings",                     path: "/settings",               group: "Administration",
    roles: ["super_admin", "institution_admin", "registrar", "programme_manager", "faculty", "instructor", "compliance_officer", "ai_governance_admin", "career_services", "training_manager", "hr_workforce_planner", "line_manager", "executive_leadership"] },
  { label: "AI Use Cases",                 path: "/ai-use-cases",           group: "Administration",
    roles: ["super_admin", "institution_admin", "ai_governance_admin", "compliance_officer"] },
  { label: "AI Governance",               path: "/governance",             group: "Administration",
    roles: ["super_admin", "institution_admin", "ai_governance_admin", "compliance_officer"] },
  { label: "Admin Guide",                  path: "/admin-guide",            group: "Administration",
    roles: ["super_admin", "institution_admin"] },
  { label: "Product Brief",               path: "/product-brief",          group: "Administration",
    roles: ["super_admin", "institution_admin"] },
];

const RECENT_KEY = "academiaos.cmdk.recent";

// Verb-first ACTIONS were tied to legacy closeout/nexus2/veda/arise backends
// that have been retired. Reintroduce them once equivalents land on the
// canonical Claros API surface.
const ACTIONS = [];

function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}

function pushRecent(path) {
  const cur = loadRecent().filter((p) => p !== path);
  cur.unshift(path);
  localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 6)));
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [recentTick, setRecentTick] = useState(0);  // bump to re-read localStorage
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const { current } = useInstitution();
  const { user } = useAuth();
  const { statusOf } = useTenantModules();
  const iid = current?.id;

  // Filter destinations by role AND module (NAV-INV-014 — mirrors Sidebar.jsx gating exactly)
  // F-002 N3-R: module gate added to match sidebar statusOf(module) === "disabled" logic
  const visibleDestinations = DESTINATIONS.filter((d) => {
    if (d.roles && !d.roles.includes(user?.role)) return false;
    if (d.module && statusOf(d.module) === "disabled") return false;
    return true;
  });

  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const recent = open ? loadRecent() : [];
  // recentTick is referenced to force re-read after pushRecent (no-op marker)
  void recentTick;

  const go = (path) => {
    pushRecent(path);
    setRecentTick((n) => n + 1);
    setOpen(false);
    navigate(path);
  };

  const runAction = async (a) => {
    if (!iid) {
      toast.error("Select an institution first");
      return;
    }
    setBusy(a.id);
    try {
      const out = await a.run({ iid });
      toast.success(`${a.label} · ${out?.msg || "done"}`);
      setOpen(false);
      if (out?.path) {
        pushRecent(out.path);
        setRecentTick((n) => n + 1);
        navigate(out.path);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `${a.label} failed`);
    } finally {
      setBusy(null);
    }
  };

  // Group destinations
  const grouped = visibleDestinations.reduce((acc, d) => {
    (acc[d.group] = acc[d.group] || []).push(d);
    return acc;
  }, {});

  const recentDests = recent
    .map((p) => visibleDestinations.find((d) => d.path === p))
    .filter(Boolean);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 hidden md:flex items-center gap-2 rounded-full border border-border bg-card/95 px-4 py-2 text-xs text-muted-foreground shadow-lg hover:bg-accent hover:text-foreground transition-colors"
        data-testid="cmdk-trigger"
        aria-label="Open command palette"
      >
        <Search className="h-3.5 w-3.5" />
        <span>Search</span>
        <kbd className="rounded border bg-background px-1.5 py-0.5 text-[10px] tabular-nums">⌘K</kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} data-testid="cmdk-dialog">
        <VisuallyHidden>
          <DialogTitle>Command Palette</DialogTitle>
          <DialogDescription>
            Fuzzy-search pages, modules and consoles. Navigate with arrow keys; press Enter to go.
          </DialogDescription>
        </VisuallyHidden>
        <CommandInput
          placeholder="Search anywhere — pages, modules, consoles…"
          data-testid="cmdk-input"
        />
        <CommandList data-testid="cmdk-list">
          <CommandEmpty>No matches.</CommandEmpty>

          {recentDests.length > 0 && (
            <>
              <CommandGroup heading="Recent">
                {recentDests.map((d) => (
                  <CommandItem
                    key={`r-${d.path}`}
                    value={`recent ${d.label}`}
                    onSelect={() => go(d.path)}
                    data-testid={`cmdk-recent-${d.path.replace(/\//g, "-")}`}
                  >
                    {d.label}
                    <span className="ml-auto text-[10px] text-muted-foreground">{d.group}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          <CommandGroup heading="Actions">
            {ACTIONS.map((a) => (
              <CommandItem
                key={a.id}
                value={`action ${a.label} ${a.keywords}`}
                onSelect={() => runAction(a)}
                disabled={busy === a.id}
                data-testid={`cmdk-action-${a.id}`}
              >
                <Zap className="h-3.5 w-3.5 mr-2 text-primary" />
                {busy === a.id ? `${a.label} · running…` : a.label}
                <span className="ml-auto text-[10px] text-muted-foreground">action</span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />

          {Object.entries(grouped).map(([group, items]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((d) => (
                <CommandItem
                  key={d.path}
                  value={`${d.label} ${d.group}`}
                  onSelect={() => go(d.path)}
                  data-testid={`cmdk-item${d.path.replace(/\//g, "-")}`}
                >
                  {d.label}
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {d.path}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
